import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Save, Mail, Phone, MessageCircle, Bell } from 'lucide-react';
import { clientAPI } from '../services/api';
import { useI18n } from '../contexts/I18nContext';
import { useCompanyNotificationTemplates } from '../hooks/useCompanyNotificationTemplates';

interface ClientNote {
  id: string;
  client_id: string;
  note: string;
  notification_type?: 'email' | 'sms' | 'whatsapp' | 'push';
  notification_contact?: string;
  scheduled_date?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

interface ClientNotesModalProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  isOpen: boolean;
  onClose: () => void;
}

const ClientNotesModal: React.FC<ClientNotesModalProps> = ({ 
  clientId, 
  clientName,
  clientEmail = '',
  clientPhone = '',
  isOpen, 
  onClose 
}) => {
  const { t } = useI18n();
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'sms' | 'whatsapp' | 'push' | ''>('');
  const [notificationContact, setNotificationContact] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingNote, setEditingNote] = useState<ClientNote | null>(null);
  const { templates, isLoading: templatesLoading } = useCompanyNotificationTemplates(
    notificationType || undefined,
  );
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.templateKey === selectedTemplateKey),
    [templates, selectedTemplateKey],
  );
  const handleTemplateSelect = useCallback((value: string) => {
    setSelectedTemplateKey(value);
    setTemplateVariables({});
  }, []);
  const handleTemplateVariableChange = useCallback((fieldKey: string, value: string) => {
    setTemplateVariables((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  }, []);
  const requiredTemplateFields = useMemo<string[]>(
    () =>
      Array.isArray(selectedTemplate?.dataSchema?.required_fields)
        ? (selectedTemplate?.dataSchema?.required_fields as string[])
        : [],
    [selectedTemplate],
  );
  const optionalTemplateFields = useMemo<string[]>(
    () =>
      Array.isArray(selectedTemplate?.dataSchema?.optional_fields)
        ? (selectedTemplate?.dataSchema?.optional_fields as string[])
        : [],
    [selectedTemplate],
  );

  const renderTemplateText = useCallback(
    (text: string) =>
      text.replace(/{{\s*([\w.\-_]+)\s*}}/g, (_, token: string) => {
        const manualValue = templateVariables[token];
        if (manualValue !== undefined && manualValue !== null && manualValue !== '') {
          return manualValue;
        }
        switch (token) {
          case 'client.name':
          case 'client_name':
            return clientName;
          case 'client.email':
          case 'client_email':
            return clientEmail;
          case 'client.phone':
          case 'client_phone':
            return clientPhone;
          default:
            return `{{${token}}}`;
        }
      }),
    [templateVariables, clientName, clientEmail, clientPhone],
  );
  const renderedTemplateBody = selectedTemplate ? renderTemplateText(selectedTemplate.body ?? '') : '';
  const renderedTemplateSubject = selectedTemplate?.subject
    ? renderTemplateText(selectedTemplate.subject)
    : undefined;

  const loadNotes = useCallback(async () => {
    if (!clientId) return;
    
    setIsLoading(true);
    try {
      const response = await clientAPI.get('/clients/notes', {
        params: { client_id: clientId }
      });
      setNotes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar lembretes:', error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen) {
      loadNotes();
    }
  }, [isOpen, loadNotes]);

  useEffect(() => {
    setSelectedTemplateKey('');
    setTemplateVariables({});
  }, [notificationType, isOpen]);

  // Preencher automaticamente o contato quando o tipo de notificação for escolhido
  useEffect(() => {
    if (notificationType === 'email' && clientEmail) {
      setNotificationContact(clientEmail);
    } else if ((notificationType === 'sms' || notificationType === 'whatsapp') && clientPhone) {
      setNotificationContact(clientPhone);
    } else if (notificationType === 'push') {
      setNotificationContact('app'); // Notificação no app
    } else {
      setNotificationContact('');
    }
  }, [notificationType, clientEmail, clientPhone]);

  const validateNote = (): string | null => {
    const hasTemplate = !!selectedTemplate;
    const manualNote = newNote.trim();
    const fallbackTemplateNote = hasTemplate ? renderedTemplateBody.trim() : '';
    const finalNote = manualNote || fallbackTemplateNote;
    if (!finalNote) {
      return 'Por favor, digite o conteúdo do lembrete ou aplique um template.';
    }

    if (finalNote.length > 2000) {
      return 'O lembrete deve ter no máximo 2000 caracteres!';
    }

    if (hasTemplate) {
      for (const fieldKey of requiredTemplateFields) {
        if (!templateVariables[fieldKey] || templateVariables[fieldKey].trim() === '') {
          return `Preencha o campo obrigatório "${fieldKey}" do template.`;
        }
      }
    }

    // Notificação é OPCIONAL - só validar se foi escolhido um tipo
    if (notificationType) {
      if (notificationType === 'email') {
        if (!notificationContact.trim()) {
          return 'Por favor, informe o email do cliente!';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notificationContact)) {
          return 'Email inválido!';
        }
      }

      if (notificationType === 'sms' || notificationType === 'whatsapp') {
        if (!notificationContact.trim()) {
          return 'Por favor, informe o telefone do cliente!';
        }
        // Validar formato de telefone (aceita vários formatos)
        const phoneDigits = notificationContact.replace(/\D/g, '');
        if (phoneDigits.length < 10 || phoneDigits.length > 15) {
          return 'Telefone inválido! Use formato: (XX) XXXXX-XXXX';
        }
      }

      // Se escolheu notificação, a data é obrigatória
      if (!scheduledDate) {
        return 'Por favor, informe quando deve ser enviado o lembrete!';
      }

      // Validar se a data não é no passado
      const selectedDate = new Date(scheduledDate);
      const now = new Date();
      if (selectedDate < now) {
        return 'A data do lembrete deve ser no futuro!';
      }
    }

    return null;
  };

  const createNote = async () => {
    const validationError = validateNote();
    if (validationError) {
      alert(validationError);
      return;
    }

    setIsLoading(true);
    try {
      // Converter data de "YYYY-MM-DDTHH:MM" para ISO8601 com segundos e timezone
      // Exemplo: "2025-10-22T11:26" -> "2025-10-22T11:26:00Z"
      const formattedDate = scheduledDate ? `${scheduledDate}:00Z` : null;
      
      // Garantir que strings vazias sejam convertidas para null
      const noteContent = (newNote.trim() || renderedTemplateBody || '').trim();
      const payload = {
        client_id: clientId,
        note: noteContent,
        notification_type: notificationType && notificationType !== '' ? notificationType : null,
        notification_contact: notificationContact && notificationContact !== '' ? notificationContact : null,
        scheduled_date: formattedDate,
        template_key: selectedTemplateKey || null,
        template_data: selectedTemplateKey ? templateVariables : null,
      };
      
      await clientAPI.post('/clients/notes', payload);
      
      // Limpar formulário
      setNewNote('');
      setNotificationType('');
      setNotificationContact('');
      setScheduledDate('');
      setSelectedTemplateKey('');
      setTemplateVariables({});
      
      await loadNotes();
      alert('✅ Lembrete criado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao criar lembrete:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Erro desconhecido';
      alert('❌ Erro ao criar lembrete: ' + errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const updateNote = async (noteId: string, updatedNote: string) => {
    if (!updatedNote.trim()) {
      alert('O lembrete não pode estar vazio!');
      return;
    }

    if (updatedNote.length > 2000) {
      alert('O lembrete deve ter no máximo 2000 caracteres!');
      return;
    }

    setIsLoading(true);
    try {
      await clientAPI.put(`/clients/notes/${noteId}`, {
        note: updatedNote.trim()
      });
      setEditingNote(null);
      await loadNotes();
    } catch (error) {
      console.error('Erro ao atualizar lembrete:', error);
      alert('Erro ao atualizar lembrete. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!confirm('Tem certeza que deseja excluir este lembrete?')) {
      return;
    }

    setIsLoading(true);
    try {
      await clientAPI.delete(`/clients/notes/${noteId}`);
      await loadNotes();
    } catch (error) {
      console.error('Erro ao deletar lembrete:', error);
      alert('Erro ao deletar lembrete. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4 text-blue-500" />;
      case 'sms': return <Phone className="w-4 h-4 text-green-500" />;
      case 'whatsapp': return <MessageCircle className="w-4 h-4 text-green-600" />;
      case 'push': return <Bell className="w-4 h-4 text-purple-500" />;
      default: return null;
    }
  };

  const getNotificationLabel = (type?: string) => {
    switch (type) {
      case 'email': return 'Email';
      case 'sms': return 'SMS';
      case 'whatsapp': return 'WhatsApp';
      case 'push': return 'Notificação Push';
      default: return 'Não definido';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '768px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Header */}
        <div 
          className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
          }}
        >
          <h2 
            className="text-2xl font-bold text-gray-800"
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1f2937',
              margin: 0
            }}
          >
            📋 Lembretes de {clientName}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '8px',
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Novo Lembrete */}
        <div 
          className="mb-8 p-4 bg-gray-50 rounded-lg"
          style={{
            marginBottom: '32px',
            padding: '20px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}
        >
          <h3 
            className="text-lg font-semibold mb-4 text-gray-700"
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus className="w-5 h-5" style={{ color: '#3b82f6' }} />
            Novo Lembrete
          </h3>

          {/* Campo de texto do lembrete */}
          <div className="mb-4">
            <label 
              className="block text-sm font-medium text-gray-700 mb-2"
              style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}
            >
              💬 Mensagem do Lembrete *
            </label>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Digite seu lembrete aqui...&#10;Exemplo: Agendar limpeza profunda, Renovar contrato, etc."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '15px',
                lineHeight: '1.6',
                resize: 'none',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                transition: 'all 0.2s',
                backgroundColor: isLoading ? '#f3f4f6' : 'white'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div 
              className="text-xs text-gray-500 mt-1"
              style={{ 
                fontSize: '12px', 
                color: newNote.length > 1900 ? '#ef4444' : '#6b7280', 
                marginTop: '6px',
                fontWeight: newNote.length > 1900 ? '600' : '400'
              }}
            >
              {newNote.length}/2000 caracteres {newNote.length > 1900 && '⚠️'}
            </div>
          </div>

          {/* Templates de notificação */}
          {notificationType && (
            <div
              style={{
                marginBottom: '16px',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                Modelos disponíveis para {notificationType.toUpperCase()}
              </label>
              {templatesLoading ? (
                <div style={{ fontSize: '14px', color: '#6b7280' }}>{t('notes.loadingTemplates')}</div>
              ) : templates.length === 0 ? (
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  Nenhum template configurado para este canal. Utilize o campo de mensagem livremente.
                </div>
              ) : (
                <select
                  value={selectedTemplateKey}
                  onChange={(event) => handleTemplateSelect(event.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    marginBottom: selectedTemplate ? '12px' : 0,
                  }}
                  disabled={isLoading}
                >
                  <option value="">Não aplicar template</option>
                  {templates.map((template) => (
                    <option key={template.templateKey} value={template.templateKey}>
                      {template.templateKey}
                      {template.subject ? ` — ${template.subject}` : ''}
                    </option>
                  ))}
                </select>
              )}

              {selectedTemplate && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                  {(requiredTemplateFields.length > 0 || optionalTemplateFields.length > 0) && (
                    <div>
                      <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '6px' }}>
                        Preencha as variáveis para personalizar o template.
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: '10px',
                        }}
                      >
                        {requiredTemplateFields.map((field) => (
                          <label
                            key={`required-${field}`}
                            style={{ fontSize: '13px', color: '#374151', display: 'flex', flexDirection: 'column', gap: '4px' }}
                          >
                            {field} <span style={{ color: '#dc2626' }}>*</span>
                            <input
                              type="text"
                              value={templateVariables[field] || ''}
                              onChange={(event) => handleTemplateVariableChange(field, event.target.value)}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            />
                          </label>
                        ))}
                        {optionalTemplateFields.map((field) => (
                          <label
                            key={`optional-${field}`}
                            style={{ fontSize: '13px', color: '#374151', display: 'flex', flexDirection: 'column', gap: '4px' }}
                          >
                            {field} <span style={{ color: '#6b7280', fontSize: '12px' }}>{t('common.optional')}</span>
                            <input
                              type="text"
                              value={templateVariables[field] || ''}
                              onChange={(event) => handleTemplateVariableChange(field, event.target.value)}
                              style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {renderedTemplateSubject && (
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      <strong>{t('notes.templatePreview')}</strong> {renderedTemplateSubject}
                    </div>
                  )}

                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                      Pré-visualização do conteúdo
                    </div>
                    <pre
                      style={{
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        padding: '12px',
                        maxHeight: '200px',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        fontSize: '13px',
                      }}
                    >
                      {renderedTemplateBody || 'Preencha os campos acima para visualizar o conteúdo.'}
                    </pre>
                    <button
                      type="button"
                      onClick={() => setNewNote(renderedTemplateBody)}
                      style={{
                        marginTop: '10px',
                        padding: '8px 14px',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        color: '#1d4ed8',
                        cursor: renderedTemplateBody ? 'pointer' : 'not-allowed',
                        fontWeight: 600,
                      }}
                      disabled={isLoading || !renderedTemplateBody}
                    >{t('notes.applyTemplate')}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tipo de Notificação */}
          <div className="mb-4">
            <label 
              className="block text-sm font-medium text-gray-700 mb-2"
              style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}
            >
              🔔 Como notificar o cliente? *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setNotificationType('email')}
                className={`p-3 border rounded-lg flex flex-col items-center justify-center gap-2 transition-all ${
                  notificationType === 'email'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                disabled={isLoading}
                style={{
                  padding: '16px 12px',
                  border: notificationType === 'email' ? '2px solid #3b82f6' : '2px solid #d1d5db',
                  borderRadius: '10px',
                  backgroundColor: notificationType === 'email' ? '#eff6ff' : 'white',
                  color: notificationType === 'email' ? '#1d4ed8' : '#374151',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: notificationType === 'email' ? '0 2px 8px rgba(59, 130, 246, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                  transform: notificationType === 'email' ? 'scale(1.02)' : 'scale(1)'
                }}
                onMouseOver={(e) => {
                  if (!isLoading && notificationType !== 'email') {
                    e.currentTarget.style.borderColor = '#9ca3af';
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseOut={(e) => {
                  if (notificationType !== 'email') {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                <Mail className="w-6 h-6" />
                <span className="text-sm font-medium" style={{ fontSize: '13px', fontWeight: '600' }}>{t('common.email')}</span>
              </button>

              {/* SMS */}
              {['sms', 'whatsapp', 'push'].map((type) => {
                const isSelected = notificationType === type;
                const config = {
                  sms: { icon: Phone, label: 'SMS', color: '#10b981', bg: '#d1fae5' },
                  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: '#059669', bg: '#d1fae5' },
                  push: { icon: Bell, label: 'Push', color: '#8b5cf6', bg: '#ede9fe' }
                };
                const Icon = config[type as keyof typeof config].icon;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setNotificationType(type as any)}
                    disabled={isLoading}
                    style={{
                      padding: '16px 12px',
                      border: isSelected ? `2px solid ${config[type as keyof typeof config].color}` : '2px solid #d1d5db',
                      borderRadius: '10px',
                      backgroundColor: isSelected ? config[type as keyof typeof config].bg : 'white',
                      color: isSelected ? config[type as keyof typeof config].color : '#374151',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column' as const,
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? `0 2px 8px ${config[type as keyof typeof config].color}33` : '0 1px 3px rgba(0,0,0,0.1)',
                      transform: isSelected ? 'scale(1.02)' : 'scale(1)'
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading && !isSelected) {
                        e.currentTarget.style.borderColor = '#9ca3af';
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <Icon className="w-6 h-6" />
                    <span style={{ fontSize: '13px', fontWeight: '600' }}>{config[type as keyof typeof config].label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Campo condicional - Email */}
          {notificationType === 'email' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email do Cliente *
              </label>
              <input
                type="email"
                value={notificationContact}
                onChange={(e) => setNotificationContact(e.target.value)}
                placeholder="cliente@exemplo.com"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Campo condicional - SMS/WhatsApp */}
          {(notificationType === 'sms' || notificationType === 'whatsapp') && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone do Cliente *
              </label>
              <input
                type="tel"
                value={notificationContact}
                onChange={(e) => setNotificationContact(e.target.value)}
                placeholder="(11) 99999-9999"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
              <div className="text-xs text-gray-500 mt-1">
                Formato: (XX) XXXXX-XXXX ou +55 XX XXXXX-XXXX
              </div>
            </div>
          )}

          {/* Campo condicional - Push */}
          {notificationType === 'push' && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700">
                <Bell className="w-4 h-4 inline mr-2" />{t('notes.mobileHint')}</p>
            </div>
          )}

          {/* Data/Hora do Lembrete */}
          {notificationType && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quando enviar o lembrete? *
              </label>
              <input
                type="datetime-local"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
          )}

          {/* Botão Adicionar */}
          <button
            onClick={createNote}
            disabled={isLoading}
            className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{
              width: '100%',
              backgroundColor: isLoading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: isLoading ? 'none' : '0 2px 4px rgba(59, 130, 246, 0.3)'
            }}
            onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#2563eb')}
            onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#3b82f6')}
          >
            <Plus className="w-5 h-5" />
            {isLoading ? 'Adicionando...' : '✨ Adicionar Lembrete'}
          </button>
        </div>

        {/* Lista de Lembretes */}
        <div style={{ marginTop: '32px' }}>
          <h3 
            className="text-lg font-semibold mb-4 text-gray-700"
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Bell className="w-5 h-5" style={{ color: '#3b82f6' }} />
            Lembretes Agendados ({notes.length})
          </h3>

          {notes.length === 0 ? (
            <div 
              className="text-center py-8 text-gray-500"
              style={{
                textAlign: 'center',
                padding: '48px 24px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px dashed #d1d5db'
              }}
            >
              <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" style={{ margin: '0 auto 12px', opacity: 0.5, color: '#9ca3af' }} />
              <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '4px' }}>Nenhum lembrete agendado ainda.</p>
              <p style={{ fontSize: '14px', color: '#9ca3af' }}>✨ Adicione o primeiro lembrete acima!</p>
            </div>
          ) : (
            <div className="space-y-3" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
                  style={{
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'}
                  onMouseOut={(e) => e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'}
                >
                  {editingNote?.id === note.id ? (
                    <div>
                      <textarea
                        defaultValue={note.note}
                        className="w-full p-2 border border-gray-300 rounded mb-2"
                        rows={3}
                        id={`edit-note-${note.id}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const textarea = document.getElementById(`edit-note-${note.id}`) as HTMLTextAreaElement;
                            updateNote(note.id, textarea.value);
                          }}
                          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />{t('actions.save')}</button>
                        <button
                          onClick={() => setEditingNote(null)}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >{t('actions.cancel')}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-gray-800 flex-1">{note.note}</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingNote(note)}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title={t('actions.edit')}
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Informações da notificação */}
                      <div className="flex flex-wrap gap-3 text-sm mt-3">
                        {/* Tipo de Notificação + Contato */}
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                          {getNotificationIcon(note.notification_type)}
                          <span className="font-medium text-gray-700">
                            {getNotificationLabel(note.notification_type)}:
                          </span>
                          {note.notification_contact && (
                            <span className="font-mono text-sm text-blue-600 font-semibold">
                              {note.notification_contact}
                            </span>
                          )}
                        </div>
                        
                        {/* Data Agendada */}
                        {note.scheduled_date && (
                          <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                            <span className="text-sm font-medium text-blue-700">
                              📅 Enviar em: {formatDateTime(note.scheduled_date)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-400 mt-2">
                        Criado em: {formatDateTime(note.created_at)}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Renderizar o modal usando Portal para garantir que fique no topo da página
  return createPortal(modalContent, document.body);
};

export default ClientNotesModal;
